import type { ReactNode } from "react";
import { PageTitle } from "@volca/ui/components/ui/page-components";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import type { OnboardingStepPane } from "@/onboarding/routes";
import { ChecklistAside } from "./checklist-aside";
import { GuideAside } from "./guide-aside";
import { useGuide } from "./guide-context";

interface StepLayoutProps {
  activePane: OnboardingStepPane;
  title: string;
  subtitle: string;
  children: ReactNode;
  /**
   * The step's Back and Continue. Owned by the shell so they sit at the bottom of the
   * page on every step, rather than under whichever column happens to be shorter.
   */
  nav?: ReactNode;
  showChecklist?: boolean;
}

// One shell for every step: the checklist beside the step, the heading, then the step's
// own content.
//
// `flex-row-reverse` puts the checklist on the right while leaving it first in the
// document, so reading order and tab order still reach it before the step it describes.
// It wraps above the step on a narrow screen, which is the order it is written in.
export const StepLayout = ({
  activePane,
  title,
  subtitle,
  children,
  nav,
  showChecklist = true,
}: StepLayoutProps) => {
  // An open guide takes its width from the page. The step keeps its column, because the
  // customer is reading the guide in order to fill the step in, and the checklist is the
  // thing they can stand to lose for a minute.
  const reading = Boolean(useGuide()?.open);

  return (
    <Stack
      gap={4}
      align="center"
      className="min-h-page w-full bg-surface-sunken px-4 py-8"
    >
      <Stack
        direction="row"
        gap={8}
        align="start"
        justify="center"
        className="w-full max-w-6xl flex-wrap-reverse lg:flex-row-reverse lg:flex-nowrap lg:justify-between"
      >
        {/* The checklist says where they are, the guides say how to do the step in front
          of them. Both belong beside the step rather than inside it. */}
        <aside
          aria-label="Onboarding checklist and guides"
          className={cn(
            "flex w-full min-w-0 flex-col gap-4 lg:w-80 lg:shrink-0",
            {
              hidden: reading || !showChecklist,
            },
          )}
        >
          <ChecklistAside activePane={activePane} />
          <GuideAside activePane={activePane} />
        </aside>

        <Stack gap={8} align="stretch" className="w-full max-w-3xl min-w-0">
          <Stack className="w-full" gap={6} align="stretch">
            <Stack className="w-full" align="start" gap={1}>
              <PageTitle>{title}</PageTitle>
              <Text tone="muted">{subtitle}</Text>
            </Stack>

            {children}
          </Stack>

          {nav}
        </Stack>
      </Stack>
    </Stack>
  );
};
